import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { useThemeColors } from "../hooks/useThemeColors";
import LoadingSpinner from "./LoadingSpinner";

/**
 * EmptyPage - A reusable component for displaying empty states, loading states, and error states
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {string} props.icon - Icon name from lucide-react (optional)
 * @param {boolean} props.loading - Whether to show loading spinner
 * @param {boolean} props.error - Whether to show error styling
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Additional content to render
 * @param {Object} props.actions - Action buttons to display
 * @param {Function} props.onRetry - Retry function for error states
 */
export function EmptyPage({
  title = "No Content",
  subtitle = "There's nothing to display here.",
  icon: IconComponent = AlertCircle,
  loading = false,
  error = false,
  className = "",
  children,
  actions,
  onRetry,
}) {
  const { themeColor } = useThemeColors();
  const baseClasses = className.includes("empty-page-fullscreen")
    ? "min-h-screen flex items-center justify-center empty-page-fullscreen"
    : "min-h-full flex items-center justify-center";
  const contentClasses = "text-center max-w-md mx-auto px-4";
  const combinedClasses = `${baseClasses} ${className}`.trim();

  // Loading state
  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  // Error state
  if (error) {
    return (
      <div className={combinedClasses}>
        <div className={contentClasses}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 mb-6">{subtitle}</p>

          {actions && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {actions}
            </div>
          )}

          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          )}

          {children}
        </div>
      </div>
    );
  }

  // Default empty state
  return (
    <div className={combinedClasses}>
      <div className={contentClasses}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 mt-12">
          <IconComponent className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">{subtitle}</p>

        {actions && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {actions}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}

/**
 * LoadingPage - Convenience component for loading states
 */
export function LoadingPage({
  title = "Loading...",
  subtitle = "Please wait while we load the content.",
}) {
  return <EmptyPage title={title} subtitle={subtitle} loading={true} />;
}

/**
 * ErrorPage - Convenience component for error states
 */
export function ErrorPage({
  title = "Something went wrong",
  subtitle = "We encountered an error while loading the content.",
  onRetry,
  actions,
}) {
  return (
    <EmptyPage
      title={title}
      subtitle={subtitle}
      error={true}
      onRetry={onRetry}
      actions={actions}
    />
  );
}

/**
 * NotFoundPage - Convenience component for 404 states
 */
export function NotFoundPage({
  title = "Page Not Found",
  subtitle = "The page you're looking for doesn't exist.",
  actions,
}) {
  return (
    <EmptyPage
      title={title}
      subtitle={subtitle}
      icon={AlertCircle}
      actions={actions}
    />
  );
}

/**
 * NoEventPage - Convenience component for when no event is selected
 */
export function NoEventPage() {
  return (
    <EmptyPage
      title="No Event Selected"
      subtitle="Please select an event to continue."
      icon={Home}
    />
  );
}

/**
 * NoItemsPage - Convenience component for when no items are available
 */
export function NoItemsPage({
  title = "No Items Available",
  subtitle = "There are no items to display at this time.",
  icon = AlertCircle,
}) {
  return <EmptyPage title={title} subtitle={subtitle} icon={icon} />;
}
